import {
  FormLabel,
  FormHelperText,
  FormControl,
  Skeleton,
  Box,
  Grid,
} from '@mui/joy'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type { InputMaskElement, Definitions } from 'imask'
import { ChangeEvent, forwardRef, KeyboardEvent, useState } from 'react'
import { Controller, ControllerProps, FieldValues } from 'react-hook-form'
import { IMaskInput } from 'react-imask'
import { Option } from '~/shared/config/constants'
import { ChipList } from '~/shared/ui/Chips'
import { SearchIcon } from '~/shared/ui/Icons'
import { Input } from '../ui/styled'

type ValidationResult = {
  isValid: boolean
  message?: string
}

type ArrayInputProps = {
  name: string
  label?: string
  placeholder?: string
  readOnly?: boolean
  skeletonShow?: boolean
  autocompleteXs: number
  rules?: ControllerProps['rules']
  validate?: (value: string) => ValidationResult
  noOptionsText?: string
  transformValue?: (value: string) => string | number
  mask?: string
  definitions?: Definitions
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
  mask?: string
  definitions?: Definitions
}

const MaskAdapter = forwardRef<HTMLElement, CustomProps>(function Wrapper(
  { onChange, mask, definitions, ...other },
  ref: InputMaskElement,
) {
  return (
    <IMaskInput
      {...other}
      mask={mask || ''}
      definitions={
        definitions || {
          '#': /[0-9]/,
        }
      }
      onInput={(value: ChangeEvent<HTMLInputElement>) => onChange(value)}
      inputRef={ref}
      autoComplete='off'
    />
  )
})

type ArrayInputFieldProps = {
  value: FieldValues
  onChange: (value: FieldValues) => void
  error?: {
    message?: string
  }
  invalid: boolean
  label?: string
  name: string
  placeholder?: string
  readOnly?: boolean
  skeletonShow?: boolean
  autocompleteXs: number
  validate?: (value: string) => ValidationResult
  transformValue?: (value: string) => string | number
  mask?: string
  definitions?: Definitions
}

function ArrayInputField({
  value,
  onChange,
  error,
  invalid,
  label,
  name,
  placeholder,
  readOnly,
  skeletonShow,
  autocompleteXs,
  validate,
  transformValue,
  mask,
  definitions,
}: ArrayInputFieldProps) {
  const [inputValue, setInputValue] = useState('')
  const [localError, setLocalError] = useState('')

  // Normalize value to always be Option array
  const normalizedValue = (value || []).map(
    (item: string | number | Option, index: number): Option => {
      if (typeof item === 'string' || typeof item === 'number') {
        return { id: `${item}-${index}`, label: String(item) }
      }
      return item
    },
  )

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault()

      // Validate input
      if (validate) {
        const result = validate(inputValue.trim())
        if (!result.isValid) {
          setLocalError(result.message || 'Неверное значение')
          return
        }
      }

      // Get current values
      const currentValues = (value || []).map((v: string | number) => String(v))

      // Check for duplicates
      const trimmedValue = inputValue.trim()
      if (currentValues.includes(trimmedValue)) {
        setLocalError('Это значение уже добавлено')
        return
      }

      // Add new value
      const newValues = [...currentValues, trimmedValue]

      // Transform values if transformer provided
      const transformedValues = newValues.map((val: string) =>
        transformValue ? transformValue(val) : val,
      )

      onChange(transformedValues)
      setInputValue('')
      setLocalError('')
    }
  }

  const hasError = invalid || localError.length > 0

  return (
    <FormControl error={hasError} component={Grid} xs={12}>
      {label && <FormLabel>{label}</FormLabel>}

      <Box
        sx={{
          marginBottom: '24px',
          minHeight: '24px',
        }}
      >
        {skeletonShow ? (
          <Skeleton
            sx={{
              position: 'relative',
              height: '24px',
              borderRadius: '12px',
            }}
          />
        ) : (
          <ChipList
            type='delete'
            onDelete={(option: Option) => {
              const filteredValues = normalizedValue.filter(
                (v: Option) => v.id !== option.id,
              )
              // Convert back to original type for form
              const transformedValues = filteredValues.map((v: Option) => {
                const label = String(v.label)
                return transformValue ? transformValue(label) : label
              })
              onChange(transformedValues)
            }}
            options={normalizedValue}
            chipProps={{
              disabled: readOnly,
            }}
          />
        )}
      </Box>

      <Box
        component={Grid}
        paddingTop={0}
        paddingLeft={0}
        paddingBottom={0}
        xs={autocompleteXs}
      >
        <Input
          fullWidth
          size='lg'
          name={`${name}-input`}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setLocalError('')
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          startDecorator={!readOnly && <SearchIcon />}
          error={hasError}
          slotProps={{
            ...(skeletonShow ? { root: { component: Skeleton } } : {}),
            ...(mask
              ? {
                  input: {
                    component: MaskAdapter,
                    mask,
                    definitions,
                  },
                }
              : {}),
          }}
        />
      </Box>

      {hasError && (
        <FormHelperText>{localError || error?.message || ''}</FormHelperText>
      )}
    </FormControl>
  )
}

export function ArrayInput({
  label,
  name,
  placeholder,
  readOnly,
  skeletonShow,
  autocompleteXs,
  rules,
  validate,
  transformValue,
  mask,
  definitions,
}: ArrayInputProps) {
  return (
    <Controller
      name={name}
      defaultValue={[]}
      rules={rules}
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => (
        <ArrayInputField
          value={value}
          onChange={onChange}
          error={error}
          invalid={invalid}
          label={label}
          name={name}
          placeholder={placeholder}
          readOnly={readOnly}
          skeletonShow={skeletonShow}
          autocompleteXs={autocompleteXs}
          validate={validate}
          transformValue={transformValue}
          mask={mask}
          definitions={definitions}
        />
      )}
    />
  )
}
